import { ArrowSquareOut, Plus, Warning } from 'phosphor-react'
import { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Col, Modal, Row, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import DashboardLayout from '../../../../layouts/Dashboard'
import api from '../../../../services/api'
import { formatDateTSX } from '../../../../utils/tsxFormat'
import { formatMoney } from '../../../../utils/format'

import styles from './styles.module.css'
import VehicleCard from '../../../../components/VehicleCard'
import { useLoading } from '../../../../hooks/LoadingContext'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../../components/SearchInput'

interface IRefuelling {
  id: number
  type: string
  vehicle_board: string
  vehicle_model: string
  vehicle_color: string
  driver_name: string
  gas_station: string
  fuel_type: string
  invoice_url: string
  previous_fuel_level_url: string
  amount: number
  date: Date
  cost: number
  mileage: number
}

interface IRefuellingCard {
  Motorista: string
  Custo: number
  Data: string
}

const gasTypeNamesMap = {
  normal_gasoline: 'gasolina comum',
  added_gasoline: 'gasolina aditivada',
  premium_gasoline: 'gasolina premium',
  formulated_gasoline: 'gasolina formulada',
  ethanol: 'etanol',
  added_ethanol: 'etanol aditivado',
  gnv: 'GNV',
  s10_diesel: 'diesel S10',
  added_diesel: 'diesel aditivado',
  premium_diesel: 'diesel premium',
}

const Abastecimentos: React.FC = () => {
  const [search, setSearch] = useState<string>('')
  const [lastSearch, setLastSearch] = useState<string>('')
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const { isLoading, setIsLoading } = useLoading()
  const [refuellings, setRefuellings] = useState<IRefuelling[]>()
  const [showRefuelling, setShowRefuelling] = useState<boolean>(false)
  const [refuelling, setRefuelling] = useState<IRefuelling>()

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl = '/delivery-manager/vehicle/refuellings?limit=20'
    if (search) apiUrl += '&search=' + search

    const offset = refuellings && !isSearchDifferent ? refuellings.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setRefuellings(data)
        setHasMoreData(false)
      } else {
        if (!refuellings) setRefuellings(data)
        else setRefuellings(isSearchDifferent ? data : refuellings.concat(data))
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, refuellings, hasMoreData])

  useEffect(() => {
    if (!refuellings && hasMoreData) loadData()
  }, [loadData, hasMoreData, refuellings])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  const navigate = useNavigate()

  const getCardData = (data: IRefuelling) => {
    let newData: IRefuellingCard = {
      Motorista: data.driver_name,
      Custo: data.cost,
      Data: formatDateTSX(data.date, "dd/MM/yyyy 'às' HH:mm:ss"),
    }

    return newData
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Abastecimentos">
      <LoadingSpinner text="Buscando abastecimentos. Aguarde..." />
      <Row style={{ marginInline: 30 }}>
        <SearchInput
          placeholder="Digite a placa do veículo ou o nome do motorista"
          searchValue={search}
          setValue={setSearch}
          handleSearch={loadData}
          showOptions={false}
        />
        <Col
          style={{ display: 'flex', marginInline: 30 }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={`${styles.newBtn} px-3 px-md-5`}
            onClick={() => navigate('/gestor-entregas/veiculos/abastecimentos/novo')}
          >
            <Plus
              style={{ marginBottom: 4, marginRight: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Novo abastecimento
          </Button>
        </Col>
      </Row>
      <Row className={`${styles.cardsRow} justify-content-center justify-content-md-start`}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!refuellings?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhum
            abastecimento encontrado!
          </Badge>
        )}
        {refuellings?.map((refuelling, index) => (
          <VehicleCard
            key={index}
            data={getCardData(refuelling)}
            id={refuelling.id}
            title={refuelling.vehicle_board}
            subtitle={`${refuelling.vehicle_model} ${refuelling.vehicle_color}`}
            seeMore={() => [setShowRefuelling(true), setRefuelling(refuelling)]}
            urlEdit={'/gestor-entregas/veiculos/abastecimentos/editar'}
          />
        ))}
      </Row>
      {showRefuelling && (
        <Modal centered size="lg" show={showRefuelling} onHide={() => setShowRefuelling(false)}>
          <Modal.Header closeButton />
          <Modal.Body style={{ marginLeft: 20, marginRight: 20 }}>
            <Row style={{ marginBottom: 15 }}>
              <span className={styles.infoSpan}>
                <strong>Veículo: </strong>
                {`${refuelling?.vehicle_model} ${refuelling?.vehicle_color}`}
              </span>
              <span className={styles.infoSpan}>
                <strong>Placa: </strong>
                {refuelling?.vehicle_board}
              </span>
              <span className={styles.infoSpan}>
                <strong>Motorista: </strong>{' '}
                {refuelling?.driver_name ? refuelling?.driver_name : 'Não informado'}
              </span>
              <span className={styles.infoSpan}>
                <strong>Posto de combustível: </strong>
                {refuelling?.gas_station}
              </span>
              <span className={styles.infoSpan}>
                <strong>Tipo de combustível: </strong>
                {gasTypeNamesMap[refuelling!.fuel_type as keyof typeof gasTypeNamesMap]}
              </span>
              <span className={styles.infoSpan}>
                <strong>Quantidade: </strong>
                {`${refuelling?.amount} litros`}
              </span>
              <span className={styles.infoSpan}>
                <strong>Quilometragem: </strong>
                {`${refuelling?.mileage} km`}
              </span>
              <span className={styles.infoSpan}>
                <strong>Custo: </strong> {formatMoney(refuelling?.cost)}
              </span>

              <span className={styles.infoSpan}>
                <strong>Data:</strong> {formatDateTSX(refuelling?.date, "dd/MM/yyyy 'às' HH:mm:ss")}
              </span>
            </Row>
            <Row>
              <Col md={6}>
                <Button
                  className={styles.modalImageBtn}
                  onClick={() => {
                    window.open(refuelling?.invoice_url, '_blank')
                  }}
                >
                  Nota fiscal
                  <ArrowSquareOut style={{ marginTop: -3, marginLeft: 3 }} size={18} />
                </Button>
              </Col>
              <Col md={6}>
                <Button
                  className={styles.modalImageBtn}
                  disabled={!refuelling?.previous_fuel_level_url}
                  onClick={() => {
                    window.open(refuelling?.previous_fuel_level_url, '_blank')
                  }}
                >
                  Nível de combustível antes do abastecimento
                  <ArrowSquareOut style={{ marginTop: -3, marginLeft: 3 }} size={18} />
                </Button>
              </Col>
            </Row>
          </Modal.Body>
          <Row className="text-center">
            <hr></hr>
            <Col>
              <Button onClick={() => setShowRefuelling(false)} className={styles.closeBtn}>
                Fechar
              </Button>
            </Col>
          </Row>
        </Modal>
      )}
    </DashboardLayout>
  )
}

export default Abastecimentos
